import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, FolderPlus, ChevronDown, ChevronRight, Folder, Braces, Trash2, Copy, Pencil, MoreHorizontal } from 'lucide-react';
import InlineNameEditor from './ui/InlineNameEditor';
import { useSidebarDrag } from '../hooks/useSidebarDrag';
import { getOrderedSchemaChildren, computeSchemaDropIndicator, getSchemaItem } from '../utils/schemaTree';
import { ConfirmModal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import ContextMenu from './ui/ContextMenu';

const SIDEBAR_TREE_ROW_IDLE =
  'border border-transparent hover:bg-gray-50 hover:border-gray-200';
const SIDEBAR_TREE_ROW_ACTIVE = 'sidebar-tree-row-active border';
const SIDEBAR_TREE_ROW_DROP = 'bg-blue-50 border border-blue-200';

const dndId = (kind, id) => (kind === 'folder' ? `folder-${id}` : `schema-${id}`);
const parseDnd = (raw) => {
  if (!raw) return null;
  if (raw.startsWith('folder-')) return { kind: 'folder', id: raw.slice(7) };
  if (raw.startsWith('schema-')) return { kind: 'schema', id: raw.slice(7) };
  return null;
};

const SchemaSidebar = ({
  schemas,
  currentWorkspaceId,
  activeSchemaId,
  onOpenSchema,
  onCreateSchema,
  onCreateSchemaFolder,
  onRenameSchemaEntry,
  onDeleteSchemaEntry,
  onDuplicateSchema,
  onMoveSchemaEntry,
  editingItem,
  onStartRename,
  onSaveRename,
  onCancelRename,
  selectedEntry,
  onSelectEntry,
  embedded = false,
  searchTerm = '',
}) => {
  const { t } = useTranslation();
  const expandKey = currentWorkspaceId
    ? `schema-sidebar-state:${currentWorkspaceId}`
    : null;
  const sectionKey = currentWorkspaceId
    ? `schema-sidebar-section:${currentWorkspaceId}`
    : null;
  const [expanded, setExpanded] = useState(() => new Set());
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [menu, setMenu] = useState(null); // { x, y, kind, id }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { kind, id, name }

  const schemasRef = useRef(schemas);
  schemasRef.current = schemas;

  useEffect(() => {
    if (!expandKey) return;
    try {
      const raw = localStorage.getItem(expandKey);
      if (!raw) return;
      const ids = JSON.parse(raw);
      if (Array.isArray(ids)) setExpanded(new Set(ids.map(String)));
    } catch {
      /* ignore corrupt state */
    }
  }, [expandKey]);

  useEffect(() => {
    if (!sectionKey) return;
    try {
      const raw = localStorage.getItem(sectionKey);
      if (raw != null) setSectionCollapsed(raw === '1');
    } catch {
      /* ignore corrupt state */
    }
  }, [sectionKey]);

  const toggleSection = useCallback(() => {
    setSectionCollapsed((prev) => {
      const next = !prev;
      if (sectionKey) {
        try {
          localStorage.setItem(sectionKey, next ? '1' : '0');
        } catch {
          /* ignore quota errors */
        }
      }
      return next;
    });
  }, [sectionKey]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (expandKey) {
        try {
          localStorage.setItem(expandKey, JSON.stringify([...next]));
        } catch {
          /* ignore quota errors */
        }
      }
      return next;
    });
  }, [expandKey]);

  const handleDragStart = useCallback((activeId) => {
    setDropIndicator(null);
    setActiveDragId(activeId);
  }, []);

  const handleDragMove = useCallback((activeId, overId, position) => {
    if (!overId) {
      setDropIndicator(null);
      return;
    }
    setDropIndicator(
      computeSchemaDropIndicator(schemasRef.current, activeId, overId, position)
    );
  }, []);

  const handleDragEnd = useCallback(
    (activeId, overId, position) => {
      if (overId && overId !== activeId && position) {
        const ind = computeSchemaDropIndicator(schemasRef.current, activeId, overId, position);
        if (ind) {
          const drag = parseDnd(activeId);
          const target = parseDnd(overId);
          if (drag && target) {
            onMoveSchemaEntry?.({
              dragKind: drag.kind,
              dragId: drag.id,
              targetKind: target.kind,
              targetId: target.id,
              position: ind.position,
            });
          }
        }
      }
      setActiveDragId(null);
      setDropIndicator(null);
    },
    [onMoveSchemaEntry]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
    setDropIndicator(null);
  }, []);

  const { getHandleProps, overlayRef, dragX, dragY } = useSidebarDrag({
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
  });

  const overlayLabel = useMemo(() => {
    if (!activeDragId) return null;
    const parsed = parseDnd(activeDragId);
    if (!parsed) return null;
    if (parsed.kind === 'folder') {
      const folder = schemas.folders?.find((f) => String(f.id) === String(parsed.id));
      return folder ? { type: 'folder', name: folder.name } : null;
    }
    const item = schemas.items?.find((it) => String(it.id) === String(parsed.id));
    return item ? { type: 'schema', name: item.name } : null;
  }, [activeDragId, schemas]);

  const menuOpenedAtRef = useRef(0);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => {
      // Ignore the trailing `click` that macOS fires from the same ctrl/right-click
      // gesture that just opened the menu (otherwise it closes instantly).
      if (Date.now() - menuOpenedAtRef.current < 250) return;
      setMenu(null);
    };
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
    };
  }, [menu]);

  /** A schema or folder's current name, for the rename prompt and the delete confirmation. */
  const entryName = (kind, id) => {
    if (kind === 'schema') {
      return getSchemaItem(schemasRef.current, id)?.name || t('schemas.newSchema');
    }
    return schemasRef.current?.folders?.find((f) => String(f.id) === String(id))?.name
      || t('schemas.newFolder');
  };

  const openMenu = (e, kind, id) => {
    e.preventDefault();
    e.stopPropagation();
    menuOpenedAtRef.current = Date.now();
    onSelectEntry?.({ kind, id });
    setMenu({ x: e.clientX, y: e.clientY, kind, id });
  };

  const query = (searchTerm || '').trim().toLowerCase();
  const searching = query.length > 0;

  // While searching, compute which folders/schemas to show: a schema matches by
  // name (or sits under a folder that matches); a folder is shown if it matches,
  // contains a match, or lives under a matching folder. Ancestors are revealed
  // so nested hits stay reachable.
  const searchVisible = useMemo(() => {
    if (!searching) return null;
    const folders = schemas?.folders || [];
    const items = schemas?.items || [];
    const folderById = new Map(folders.map((f) => [String(f.id), f]));

    const ancestors = (folderId) => {
      const out = [];
      let cur = folderId != null ? folderById.get(String(folderId)) : null;
      const guard = new Set();
      while (cur && !guard.has(String(cur.id))) {
        guard.add(String(cur.id));
        out.push(String(cur.id));
        cur = cur.parent_id != null ? folderById.get(String(cur.parent_id)) : null;
      }
      return out;
    };

    const folderSelfMatch = new Set(
      folders
        .filter((f) => String(f.name || '').toLowerCase().includes(query))
        .map((f) => String(f.id))
    );
    const underMatchingFolder = (folderId) =>
      ancestors(folderId).some((fid) => folderSelfMatch.has(fid));

    const vFolders = new Set();
    const vSchemas = new Set();

    for (const it of items) {
      const self = String(it.name || '').toLowerCase().includes(query);
      if (self || underMatchingFolder(it.folder_id)) {
        vSchemas.add(String(it.id));
        ancestors(it.folder_id).forEach((fid) => vFolders.add(fid));
      }
    }
    for (const f of folders) {
      if (folderSelfMatch.has(String(f.id)) || underMatchingFolder(f.parent_id)) {
        vFolders.add(String(f.id));
        ancestors(f.parent_id).forEach((fid) => vFolders.add(fid));
      }
    }

    return { vFolders, vSchemas };
  }, [searching, query, schemas]);

  const renderRow = (entry) => {
    const { type, item } = entry;
    const id = item.id;
    const kind = type === 'folder' ? 'folder' : 'schema';
    const rawId = dndId(kind, id);
    const isEditing = editingItem && editingItem.kind === kind && String(editingItem.id) === String(id);
    const isDragging = activeDragId === rawId;
    const ind = dropIndicator?.overId === rawId ? dropIndicator : null;
    const dropBefore = ind?.position === 'before';
    const dropAfter = ind?.position === 'after';
    const isDropZone = ind?.position === 'inside';
    // Folders can never be the "active schema tab" row.
    // When a schema entry is selected: suppress isActive only for that specific
    // row (shown via isSelected instead); other schemas still show the tab indicator.
    // When a folder entry is selected: suppress isActive for all schema rows to
    // prevent a folder highlight + schema tab highlight appearing simultaneously.
    const isActive =
      kind !== 'folder' &&
      (!selectedEntry || (selectedEntry.kind === 'schema' && String(selectedEntry.id) !== String(id))) &&
      String(activeSchemaId) === String(id);
    const isSelected =
      selectedEntry && selectedEntry.kind === kind && String(selectedEntry.id) === String(id);

    if (searching && searchVisible) {
      const set = kind === 'folder' ? searchVisible.vFolders : searchVisible.vSchemas;
      if (!set.has(String(id))) return null;
    }

    if (type === 'folder') {
      const isOpen = searching ? true : expanded.has(id);
      return (
        <div key={`f-${id}`} className="sidebar-row-wrap rounded-md">
          {dropBefore && <div className="sidebar-drop-line" aria-hidden />}
          <div
            data-drag-id={rawId}
            style={{ paddingLeft: '4px', opacity: isDragging ? 0.4 : undefined }}
            className={`flex items-center px-2 py-1 rounded-md group cursor-pointer transition-colors duration-150 ${
              isDropZone
                ? SIDEBAR_TREE_ROW_DROP
                : isSelected
                  ? SIDEBAR_TREE_ROW_ACTIVE
                  : SIDEBAR_TREE_ROW_IDLE
            }`}
            onClick={() => {
              onSelectEntry?.({ kind: 'folder', id });
            }}
            onContextMenu={(e) => openMenu(e, 'folder', id)}
            {...(!isEditing && !searching ? getHandleProps(rawId) : {})}
          >
            <button
              type="button"
              className="sidebar-collection-chevron-btn mr-1 shrink-0 flex items-center p-0.5 rounded"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (!searching) toggle(id);
              }}
              aria-label={isOpen ? t('sidebar.collapseFolder') : t('sidebar.expandFolder')}
            >
              {isOpen ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
            </button>
            <Folder size={12} className="text-amber-600 shrink-0 mr-1" aria-hidden />
            {isEditing ? (
              <InlineNameEditor
                value={item.name}
                onSave={(name) => onSaveRename?.('folder', id, name)}
                onCancel={() => onCancelRename?.()}
              />
            ) : (
              <span className="flex-1 min-w-0 text-xs font-medium text-gray-800 dark:text-zinc-200 truncate">
                {item.name}
              </span>
            )}
            <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-1">
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateSchema?.(id);
                }}
                className="shrink-0 p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer"
                title={t('schemas.newSchema')}
              >
                <Plus size={12} className="text-gray-500 dark:text-zinc-400" />
              </button>
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  openMenu(e, 'folder', id);
                }}
                className="shrink-0 p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer"
              >
                <MoreHorizontal size={12} className="text-gray-500 dark:text-zinc-400" />
              </button>
            </div>
          </div>
          {isOpen && (
            <div className="sidebar-folder-scope">
              {getOrderedSchemaChildren(schemas, id).map((c) => renderRow(c))}
            </div>
          )}
          {dropAfter && <div className="sidebar-drop-line" aria-hidden />}
        </div>
      );
    }

    return (
      <div key={`s-${id}`} className="sidebar-row-wrap">
        {dropBefore && <div className="sidebar-drop-line" aria-hidden />}
        <div
          data-drag-id={rawId}
          style={{ opacity: isDragging ? 0.4 : undefined }}
          className={`flex items-center px-2 py-1 rounded-md group transition-all duration-200 cursor-pointer ${
            isActive || isSelected ? SIDEBAR_TREE_ROW_ACTIVE : SIDEBAR_TREE_ROW_IDLE
          }`}
          onClick={() => {
            onSelectEntry?.({ kind: 'schema', id });
            onOpenSchema?.(item);
          }}
          onContextMenu={(e) => openMenu(e, 'schema', id)}
          {...(!isEditing && !searching ? getHandleProps(rawId) : {})}
        >
          <Braces size={12} className="text-gray-500 shrink-0 mr-1" aria-hidden />
          {isEditing ? (
            <InlineNameEditor
              value={item.name}
              className="text-xs font-normal"
              onSave={(name) => onSaveRename?.('schema', id, name)}
              onCancel={() => onCancelRename?.()}
            />
          ) : (
            <span className="flex-1 min-w-0 text-xs text-gray-800 dark:text-zinc-200 truncate pointer-events-none">
              {item.name}
            </span>
          )}
          <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-1">
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                openMenu(e, 'schema', id);
              }}
              className="shrink-0 p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer"
            >
              <MoreHorizontal size={12} className="text-gray-500 dark:text-zinc-400" />
            </button>
          </div>
        </div>
        {dropAfter && <div className="sidebar-drop-line" aria-hidden />}
      </div>
    );
  };

  const roots = getOrderedSchemaChildren(schemas, null);

  // Render rows up front and drop the ones filtered out by search (renderRow
  // returns null) so we never emit empty wrapper divs / stray `space-y-1` gaps.
  const renderedRoots = roots
    .map((entry) => ({ id: entry.id, node: renderRow(entry) }))
    .filter((r) => r.node);

  // During a search, nothing under the section matches when no rows survive.
  const noSearchMatches = searching && renderedRoots.length === 0;

  const listRows = (
    <div className="space-y-1">
      {renderedRoots.map(({ id, node }) => (
        <div key={id} className="animate-fadeIn">
          {node}
        </div>
      ))}
    </div>
  );

  const sectionBody =
    roots.length === 0 ? (
      <div className="py-8 text-center">
        <div className="sidebar-empty-icon-wrap">
          <div className="sidebar-empty-icon">
            <Braces size={16} />
          </div>
        </div>
        <p className="mb-1 text-xs font-medium text-gray-600 dark:text-zinc-200">{t('schemas.empty')}</p>
        <p className="text-xs text-gray-500 dark:text-zinc-400">{t('schemas.emptyHint')}</p>
      </div>
    ) : noSearchMatches ? (
      <p className="px-2 py-3 text-xs text-gray-400 dark:text-zinc-500">
        {t('sidebar.noResultsFound')}
      </p>
    ) : (
      listRows
    );

  return (
    <>
      {embedded ? (
        <div className="pt-1" onContextMenu={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between pb-1 group/section">
            <button
              type="button"
              onClick={toggleSection}
              style={{ marginLeft: '-6px' }}
              className="flex items-center gap-2 flex-1 min-w-0 bg-transparent p-0 cursor-pointer"
            >
              {sectionCollapsed ? (
                <ChevronRight size={14} className="text-gray-400 shrink-0" aria-hidden />
              ) : (
                <ChevronDown size={14} className="text-gray-400 shrink-0" aria-hidden />
              )}
              <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide dark:text-zinc-400">
                {t('schemas.title')}
              </h2>
            </button>
            <div className="flex items-center space-x-1">
              <button
                type="button"
                className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded bg-transparent transition-colors duration-150"
                title={t('schemas.newFolder')}
                onClick={() => onCreateSchemaFolder?.(null)}
              >
                <FolderPlus size={14} />
              </button>
              <button
                type="button"
                className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded bg-transparent transition-colors duration-150"
                title={t('schemas.newSchema')}
                onClick={() => onCreateSchema?.(null)}
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
          {(!sectionCollapsed || searching) && sectionBody}
        </div>
      ) : (
        <div className="w-full h-full bg-gray-50 flex flex-col">
          <div className="flex-shrink-0 px-4 pt-2 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                {t('schemas.title')}
              </h2>
              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg bg-transparent transition-colors duration-150"
                  title={t('schemas.newFolder')}
                  onClick={() => onCreateSchemaFolder?.(null)}
                >
                  <FolderPlus size={16} />
                </button>
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg bg-transparent transition-colors duration-150"
                  title={t('schemas.newSchema')}
                  onClick={() => onCreateSchema?.(null)}
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </div>

          <OsScroll className="flex-1 min-h-0 px-3 pb-3">
            {roots.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Braces size={24} className="opacity-50" />
                </div>
                <p className="text-gray-600 font-medium mb-2">{t('schemas.empty')}</p>
              </div>
            ) : noSearchMatches ? (
              <p className="px-2 py-3 text-xs text-gray-400 dark:text-zinc-500 text-center">
                {t('sidebar.noResultsFound')}
              </p>
            ) : (
              listRows
            )}
          </OsScroll>
        </div>
      )}

      {activeDragId && overlayLabel && (
        <div
          ref={overlayRef}
          style={{
            position: 'fixed',
            left: dragX,
            top: dragY,
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          {overlayLabel.type === 'folder' ? (
            <div className="flex w-[min(100%,260px)] items-center gap-1 rounded-md sidebar-tree-row-active border px-2 py-1 text-xs font-medium text-gray-900 shadow-lg dark:text-zinc-200">
              <Folder size={14} className="shrink-0 text-amber-600" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{overlayLabel.name}</span>
            </div>
          ) : (
            <div className="flex w-[min(100%,260px)] items-center gap-1 rounded-md sidebar-tree-row-active border px-2 py-1 text-xs font-medium text-gray-900 shadow-lg dark:text-zinc-200">
              <Braces size={14} className="shrink-0 text-gray-500" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{overlayLabel.name}</span>
            </div>
          )}
        </div>
      )}

      {/* The one context menu, portaled. This used to be a hand-rolled copy
          rendered INSIDE the sidebar, so `fixed z-50` was still trapped in the
          sidebar's stacking context and the content pane painted over it
          (owner, 22.09.2026). `ui/ContextMenu` portals to <body>, clamps to the
          viewport and closes on outside click / Escape. */}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            menu.kind === 'folder' && {
              icon: <Plus size={14} />,
              label: t('schemas.newSchema'),
              onClick: () => onCreateSchema?.(menu.id),
            },
            menu.kind === 'folder' && {
              icon: <FolderPlus size={14} />,
              label: t('schemas.newSubfolder'),
              onClick: () => onCreateSchemaFolder?.(menu.id),
            },
            {
              icon: <Copy size={14} />,
              label: t('schemas.duplicate'),
              onClick: () => onDuplicateSchema?.(menu.kind, menu.id),
            },
            {
              icon: <Pencil size={14} />,
              label: t('schemas.rename'),
              onClick: () => onStartRename?.(menu.kind, { id: menu.id, name: entryName(menu.kind, menu.id) }),
            },
            {
              icon: <Trash2 size={14} />,
              label: t('schemas.delete'),
              danger: true,
              onClick: () => setDeleteConfirm({ kind: menu.kind, id: menu.id, name: entryName(menu.kind, menu.id) }),
            },
          ]}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            onDeleteSchemaEntry?.(deleteConfirm.kind, deleteConfirm.id);
          }
          setDeleteConfirm(null);
        }}
        title={deleteConfirm?.kind === 'folder' ? t('schemas.deleteFolder') : t('schemas.deleteSchema')}
        message={t('schemas.deleteConfirm', { name: deleteConfirm?.name || '' })}
        confirmText={t('schemas.delete')}
        type="danger"
      />
    </>
  );
};

export default SchemaSidebar;
